import { useMemo, type SVGProps } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GlassBottomNavigation, type GlassNavItem } from "@akuna/ui";
import { useFilteredNavigation } from "@/hooks/useFilteredNavigation";
import { AccountMenuItems } from "@/components/navigation/AccountMenuItems";
import { CustomIcons, type CustomIconName } from "@/components/icons/CustomIcons";
import { cn } from "@/lib/utils";
import {
  MAIN_NAV_ITEMS,
  getMainNavTargetPath,
  isMainNavActive,
  getNavItemLabel,
} from "@/components/navigation/main-nav-items";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { useTranslation } from "react-i18next";

const PRIMARY_MOBILE_PATHS = ["/patients", "/calendar", "/calendar/flowboard", "/catalog"];

function createNavIcon(name: CustomIconName) {
  return function NavIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
    return <CustomIcons {...props} name={name} className={cn("text-current", className)} />;
  };
}

export function BottomNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t: tNav } = useTranslation("nav");

  const { sections, isLoading } = useFilteredNavigation();
  const { labelKey: daycareHotelLabelKey } = useDaycareHotelAccess();

  const visibleNavItems = useMemo(() => {
    if (isLoading) return [];

    const visibleUrls = new Set(
      sections.flatMap((section) => section.items.map((item) => item.url))
    );

    return MAIN_NAV_ITEMS.filter((item) => visibleUrls.has(item.path));
  }, [sections, isLoading]);

  const canAccessGrooming = useMemo(() => {
    if (isLoading) return false;

    const visibleUrls = new Set(
      sections.flatMap((section) => section.items.map((item) => item.url))
    );

    return visibleUrls.has("/grooming");
  }, [sections, isLoading]);

  const items = useMemo<GlassNavItem[]>(() => (
    PRIMARY_MOBILE_PATHS
      .map((path) => visibleNavItems.find((item) => item.path === path))
      .filter((item): item is (typeof visibleNavItems)[number] => Boolean(item))
      .map((item) => {
      const targetPath = getMainNavTargetPath(item.path, canAccessGrooming);
      const shortLabel = {
        "/calendar/flowboard": "flow",
      }[item.path] ?? item.label;

      return {
        value: item.path,
        label: tNav(shortLabel),
        icon: createNavIcon(item.icon),
        href: targetPath,
        onClick: (event) => {
          event.preventDefault();
          navigate(targetPath);
        },
      };
    })
  ), [canAccessGrooming, navigate, tNav, visibleNavItems]);

  const moreItems = useMemo<GlassNavItem[]>(() => (
    visibleNavItems
      .filter((item) => !PRIMARY_MOBILE_PATHS.includes(item.path))
      .map((item) => {
        const targetPath = getMainNavTargetPath(item.path, canAccessGrooming);

        return {
          value: item.path,
          label: getNavItemLabel(item, tNav, daycareHotelLabelKey),
          icon: createNavIcon(item.icon),
          href: targetPath,
          onClick: (event) => {
            event.preventDefault();
            navigate(targetPath);
          },
        };
      })
  ), [canAccessGrooming, daycareHotelLabelKey, navigate, tNav, visibleNavItems]);

  const activeValue = visibleNavItems.find((item) => isMainNavActive(item.path, location.pathname))?.path;

  if (items.length === 0 || isLoading) {
    return <div className="md:hidden h-24" />;
  }

  return (
    <>
      <div className="md:hidden fixed inset-x-0 bottom-0 z-50 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <GlassBottomNavigation
          accountFooter={<AccountMenuItems />}
          items={items}
          label={tNav("bottomNavigationLabel")}
          maxVisible={4}
          moreItems={moreItems}
          moreLabel={tNav("menu")}
          value={activeValue}
        />
      </div>
    </>
  );
}
